import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { UserService } from '../../../service/UserService';
import { Auth } from '@angular/fire/auth';
import { DataService } from '../../../service/data.service';

@Component({
  selector: 'app-order-total',
  templateUrl: './order-total.component.html',
  styleUrls: ['./order-total.component.scss']
})
export class OrderTotalComponent implements OnInit, OnChanges {

  @Input() total: number = 0;
  @Input() envio: any;
  @Input() granTotal: any;
  @Input() productCount: any;
  
  // Propiedades locales para manejar el estado después del cupón
  envioActual: number = 0;
  granTotalActual: number = 0;
  descuentoAplicado: number = 0;
  
  cupon: string = '';
  
  // Para los mensajes de la interfaz
  mensaje: string = '';
  tipoMensaje: 'success' | 'danger' | '' = '';

  // Dialog state when prices/stock changed
  showPriceChangeDialog: boolean = false;
  changedItems: Array<any> = [];
  updatedGranTotal: number = 0;

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private userService: UserService,
    private auth: Auth,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.envioActual = this.envio;
    this.granTotalActual = this.granTotal;
    
    // Sincronizar con localStorage al iniciar
    const storedCupon = localStorage.getItem('cupon');
    if (storedCupon) {
      this.cupon = storedCupon;
      this.descuentoAplicado = Number(localStorage.getItem('descuento') || 0);
      this.granTotalActual = Number(localStorage.getItem('granTotal') || this.granTotalActual);
    } else {
      // Inicializamos los valores en localStorage
      localStorage.setItem('subtotal', String(this.total));
      localStorage.setItem('granTotal', String(this.granTotalActual));
      localStorage.setItem('descuento', String(this.descuentoAplicado));
      localStorage.setItem('cupon', this.cupon);
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['total'] && !changes['total'].firstChange) {
      this.envioActual = this.envio;
      this.granTotalActual = this.total + this.envioActual;
      
      this.descuentoAplicado = 0;
      this.cupon = '';
      this.mensaje = '';
      
      // Actualizamos localStorage con los nuevos valores
      localStorage.setItem('subtotal', String(this.total));
      localStorage.setItem('granTotal', String(this.granTotalActual));
      localStorage.setItem('descuento', String(this.descuentoAplicado));
      localStorage.setItem('cupon', '');
    }
  }

  onCuponInputChange(): void {
    this.descuentoAplicado = 0;
    this.granTotalActual = this.total + this.envio;
    this.mensaje = '';
    
    // Actualizamos localStorage cuando el input cambia y reiniciamos el cupón
    localStorage.setItem('granTotal', String(this.granTotalActual));
    localStorage.setItem('cupon', '');
    localStorage.setItem('descuento', '0');
  }
  
  regresar(): void {
    this.router.navigate(['/product']);
  }

  aplicarCupon(): void {
    this.mensaje = '';
    
    if (!this.cupon) {
      this.mostrarMensaje('Por favor, ingresa un código de cupón.', 'danger');
      return;
    }

    // Before applying coupon or proceeding to payment, validate cart with server
    // We need to get DataService — use userService (already injected) to access dataService if available
    // But better: inject DataService here. We'll add it to constructor.
  }

  // Método público que valida el carrito con el servidor antes de proceder al pago
  proceedToPayment(): void {
    this.mensaje = '';

    // **CAPTURAR datos de personalización ANTES de validar con servidor**
    const localCartBefore = JSON.parse(localStorage.getItem('cartItems') || '[]');
    const personalizationMap = new Map();
    
    localCartBefore.forEach((item: any) => {
      if (item.personalized && item.selectedSize) {
        personalizationMap.set(
          item.id,
          {
            namePerson: item.selectedSize.namePerson,
            position: item.selectedSize.position,
            bloodType: item.selectedSize.bloodType,
            claseInProgress: item.selectedSize.claseInProgress,
            club: item.selectedSize.club,
            yearsService: item.selectedSize.yearsService
          }
        );
      }
    });

    // Llamamos al DataService que devuelve el carrito validado y actualizado
    this.dataService.validateCartWithServer().subscribe((updatedCart) => {
      // Compare against the snapshot taken before the service ran
      const localCart = Array.isArray(localCartBefore) ? localCartBefore : [];

      // **RESTAURAR datos de personalización en updatedCart**
      updatedCart.forEach((item: any) => {
        const personalization = personalizationMap.get(item.id);
        if (personalization && item.selectedSize) {
          item.selectedSize = {
            ...item.selectedSize,
            namePerson: personalization.namePerson,
            position: personalization.position,
            bloodType: personalization.bloodType,
            claseInProgress: personalization.claseInProgress,
            club: personalization.club,
            yearsService: personalization.yearsService
          };
        }
      });

      // **GUARDAR carrito restaurado en localStorage**
      localStorage.setItem('cartItems', JSON.stringify(updatedCart));

      // Detectar cambios: longitud, ids, cantidades o precios diferentes
      let changesDetected = false;

      if (!Array.isArray(updatedCart)) {
        this.mostrarMensaje('No se pudo validar el carrito. Intenta nuevamente.', 'danger');
        this.router.navigate(['/cart']);
        return;
      }

      if (updatedCart.length !== localCart.length) {
        changesDetected = true;
      } else {
        // Comparar item a item por idTable o id
        for (let i = 0; i < updatedCart.length; i++) {
          const serverItem: any = updatedCart[i];
          const localItem: any = localCart.find((it: any) => (it.idTable || it.id) === (serverItem.idTable || serverItem.id));
          if (!localItem) {
            changesDetected = true;
            break;
          }

          const localQty = Number(localItem.quantity || 1);
          const serverQty = Number(serverItem.quantity || 1);
          if (localQty !== serverQty) {
            changesDetected = true;
            break;
          }

          const localPrice = Number(localItem.selectedSize?.price || localItem.priceShop || localItem.minPrice || 0);
          const serverPrice = Number(serverItem.selectedSize?.price ?? serverItem.priceShop ?? serverItem.minPrice ?? 0);
          if (localPrice !== serverPrice) {
            changesDetected = true;
            break;
          }
        }
      }

      if (changesDetected) {
        // Construir lista de cambios para mostrar en el diálogo
        const diffs: any[] = [];
        for (const serverItem of updatedCart) {
          const localItem: any = localCart.find((it: any) => (it.idTable || it.id) === (serverItem.idTable || serverItem.id));

          // Prepare display values
          const imageUrl = serverItem.coverImage || (serverItem.images && serverItem.images[0]) || localItem?.coverImage || (localItem?.images && localItem.images[0]) || '';
          const localColor = localItem?.selectedColor?.name || '';
          const serverColor = serverItem?.selectedColor?.name || '';
          const displayColor = localColor || serverColor || '';
          const localSize = localItem?.selectedSize?.size || '';
          const serverSize = serverItem?.selectedSize?.size || '';
          const displaySize = localSize || serverSize || '';

          if (!localItem) {
            diffs.push({
              title: serverItem.product || serverItem.model || 'Producto',
              type: 'removed',
              oldQty: localItem?.quantity || 0,
              newQty: 0,
              oldPrice: localItem ? Number(localItem.selectedSize?.price || localItem.priceShop || localItem.minPrice || 0) : 0,
              newPrice: Number(serverItem.selectedSize?.price ?? serverItem.priceShop ?? serverItem.minPrice ?? 0),
              image: imageUrl,
              color: displayColor,
              size: displaySize
            });
            continue;
          }

          const localPrice = Number(localItem.selectedSize?.price || localItem.priceShop || localItem.minPrice || 0);
          const serverPrice = Number(serverItem.selectedSize?.price ?? serverItem.priceShop ?? serverItem.minPrice ?? 0);
          const localQty = Number(localItem.quantity || 1);
          const serverQty = Number(serverItem.quantity || 1);

          if (localPrice !== serverPrice || localQty !== serverQty) {
            diffs.push({
              title: serverItem.product || serverItem.model || 'Producto',
              type: 'changed',
              oldQty: localQty,
              newQty: serverQty,
              oldPrice: localPrice,
              newPrice: serverPrice,
              image: imageUrl,
              color: displayColor,
              size: displaySize
            });
          }
        }

        // Calcular nuevo granTotal con el carrito actualizado (ahora incluye envío y descuento)
        const subtotalFromUpdated = updatedCart.reduce((acc: number, it: any) => {
          const price = Number(it.selectedSize?.price || it.price || 0);
          const qty = Number(it.quantity || 1);
          return acc + price * qty;
        }, 0);

        // Use current envioActual and descuentoAplicado (fallback to stored values)
        const envioVal = Number(this.envioActual || Number(localStorage.getItem('envio') || 0));
        const descuentoVal = Number(this.descuentoAplicado || Number(localStorage.getItem('descuento') || 0));

        const newGranTotal = subtotalFromUpdated + envioVal - descuentoVal;

        this.changedItems = diffs;
        this.updatedGranTotal = newGranTotal;
        this.granTotalActual = newGranTotal;
        this.descuentoAplicado = descuentoVal;

        // Mostrar diálogo para informar al usuario de los cambios y permitir acción
        this.showPriceChangeDialog = true;
        this.mostrarMensaje('Se actualizaron precios o disponibilidad en tu carrito.', 'danger');
        return;
      } else {
        // No hubo cambios -> proceder al pago
        this.router.navigate(['/payments']);
      }
    }, (err) => {
      console.error('Error validating cart before payment:', err);
      this.mostrarMensaje('Error al validar el carrito. Intenta nuevamente.', 'danger');
    });
  }

  // Opciones del diálogo
  goToCartFromDialog(): void {
    this.showPriceChangeDialog = false;
    this.router.navigate(['/cart']);
  }

  continueToPaymentFromDialog(): void {
    this.showPriceChangeDialog = false;
    // Actualizar valores en localStorage con el nuevo carrito/total
    try {
      // Ensure subtotal matches updated cart
      const updatedCart = JSON.parse(localStorage.getItem('cartItems') || '[]');
      const subtotal = updatedCart.reduce((acc: number, it: any) => {
        const price = Number(it.selectedSize?.price || it.priceShop || it.minPrice || 0);
        const qty = Number(it.quantity || 1);
        return acc + price * qty;
      }, 0);
      localStorage.setItem('subtotal', String(subtotal));

      // Compute grand total including shipping and discount
      const envioVal = Number(this.envioActual || Number(localStorage.getItem('envio') || 0));
      const descuentoVal = Number(this.descuentoAplicado || Number(localStorage.getItem('descuento') || 0));
      const grandTotalToStore = subtotal + envioVal - descuentoVal;
      localStorage.setItem('granTotal', String(grandTotalToStore));
    } catch (e) {
      console.error('Error updating totals in localStorage before payment', e);
    }
    // El carrito ya fue actualizado por validateCartWithServer y guardado en localStorage
    this.router.navigate(['/payments']);
  }

  private mostrarMensaje(mensaje: string, tipo: 'success' | 'danger'): void {
    this.mensaje = mensaje;
    this.tipoMensaje = tipo;
  }
}